import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

import { apiService } from '../../../../components/api/apiService';
import { llmConfigurationService, type LLMConfiguration } from '../../../../components/api/llmConfigurationService';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  /** When set, modal is in edit mode and will call PUT instead of POST */
  initialData?: LLMConfiguration | null;
}

interface LLMProviderType {
  id: string;
  name: string;
  providerType: number;
  description?: string;
}

const parseDisplayName = (additionalConfigJson?: string): string => {
  if (!additionalConfigJson) return '';
  try {
    const o = JSON.parse(additionalConfigJson);
    return o?.displayName ?? '';
  } catch {
    return '';
  }
};

const AddLLMConfigModal: React.FC<Props> = ({ onClose, onSubmit, initialData }) => {
  const isEdit = Boolean(initialData?.id);
  const [providers, setProviders] = useState<LLMProviderType[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const [tenantIdUsed, setTenantIdUsed] = useState<string | null>(null);
  const [name, setName] = useState(initialData ? parseDisplayName(initialData.additionalConfigJson) || initialData.modelName || '' : '');
  const [provider, setProvider] = useState(initialData?.provider ?? '');
  const [model, setModel] = useState(initialData?.modelName ?? '');
  const [endpointUrl, setEndpointUrl] = useState(initialData?.endpointUrl ?? '');
  const [region, setRegion] = useState(initialData?.region ?? '');
  const [apiVersion, setApiVersion] = useState(initialData?.apiVersion ?? '');
  const [costPreference, setCostPreference] = useState(initialData?.costPreference ?? 'balanced');
  const [temperature, setTemperature] = useState(initialData?.temperature ?? 0.7);
  const [topP, setTopP] = useState(initialData?.topP ?? 1);
  const [maxTokens, setMaxTokens] = useState<number | ''>(initialData?.maxTokens ?? '');
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState<number | ''>(initialData?.rateLimitPerMinute ?? '');
  const [apiKey, setApiKey] = useState(initialData?.apiKey ?? '');
  const [status, setStatus] = useState(initialData?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const fetchProviders = async () => {
      setProvidersLoading(true);
      setProvidersError(null);
      setTenantIdUsed(null);
      try {
        const { data, headers } = await apiService.getWithHeaders('/api/LLMProviderType/GetAll', {}, 'backend');
        const list = Array.isArray(data) ? data : (data?.data ?? data ?? []);
        setProviders(list);
        const tenantId = headers?.['x-tenant-id'] ?? headers?.['X-Tenant-Id'];
        if (tenantId) setTenantIdUsed(tenantId);
      } catch (error) {
        console.error('Failed to fetch LLM providers:', error);
        setProvidersError('Could not load providers. Check your connection.');
      } finally {
        setProvidersLoading(false);
      }
    };
    fetchProviders();

    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSubmit = async () => {
    if (!name || !provider || !model || !apiKey?.trim()) return;
    setSaveError(null);
    setSaving(true);
    try {
      const additionalConfigJson = name ? JSON.stringify({ displayName: name }) : undefined;
      const dto = {
        provider,
        modelName: model,
        temperature,
        topP,
        maxTokens: maxTokens === '' ? undefined : Number(maxTokens),
        rateLimitPerMinute: rateLimitPerMinute === '' ? undefined : Number(rateLimitPerMinute),
        apiKey: apiKey || undefined,
        endpointUrl: endpointUrl || undefined,
        region: region || undefined,
        apiVersion: apiVersion || undefined,
        costPreference: costPreference || undefined,
        isDefault: initialData?.isDefault ?? false,
        isActive: status,
        additionalConfigJson,
      };
      if (isEdit && initialData?.id) {
        await llmConfigurationService.update(initialData.id, dto);
      } else {
        await llmConfigurationService.create(dto);
      }
      onSubmit();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-neutral-900 w-full max-w-2xl rounded-xl shadow-xl border border-neutral-800 flex flex-col max-h-[90vh] my-auto">
        {/* Header */}
        <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="text-white text-lg font-semibold">{isEdit ? 'Edit LLM Configuration' : 'Add New LLM Configuration'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form - scrollable so footer stays visible */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5 text-sm text-white">
          <div>
            <label className="text-gray-400 block mb-1">Configuration Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Support LLM"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 block mb-1">Provider (LLM type)</label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                disabled={providersLoading}
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Select LLM provider type"
              >
                <option value="">
                  {providersLoading ? 'Loading providers…' : 'Select Provider'}
                </option>
                {!providersLoading && providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.description ? `${p.name} – ${p.description}` : p.name}
                  </option>
                ))}
              </select>
              {!providersLoading && providers.length === 0 && !providersError && (
                <p className="mt-1 text-xs text-amber-500">No provider types available.</p>
              )}
              {providersError && (
                <p className="mt-1 text-xs text-red-400">{providersError}</p>
              )}
              {tenantIdUsed && !providersError && (
                <p className="mt-1 text-xs text-gray-500" title="Tenant used for this list">
                  For your organization {process.env.NODE_ENV === 'development' && `(tenant: ${tenantIdUsed})`}
                </p>
              )}
            </div>
            <div>
              <label className="text-gray-400 block mb-1">Model</label>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="e.g. gpt-4, claude-3, mistral-7b"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-gray-400 block mb-1">Endpoint URL (Optional)</label>
            <input
              type="url"
              value={endpointUrl}
              onChange={(e) => setEndpointUrl(e.target.value)}
              placeholder="e.g. https://your-resource.openai.azure.com"
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 block mb-1">Region (Optional)</label>
              <input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. East US, EU West"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">API Version (Optional)</label>
              <input
                type="text"
                value={apiVersion}
                onChange={(e) => setApiVersion(e.target.value)}
                placeholder="e.g. 2023-07-01-preview"
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-gray-400 block mb-1">Cost Preference</label>
            <select
              value={costPreference}
              onChange={(e) => setCostPreference(e.target.value)}
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            >
              <option value="balanced">Balanced</option>
              <option value="Low Cost">Low Cost</option>
              <option value="High Accuracy">High Accuracy</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-400 block mb-1">Temperature</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">API Key</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-gray-400 hover:text-white text-sm"
          >
            {showAdvanced ? 'Hide' : 'Show'} advanced (Max Tokens, TopP, Rate limit)
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-2 gap-4 rounded bg-neutral-800/50 p-3">
              <div>
                <label className="text-gray-400 block mb-1">Max Tokens (Optional)</label>
                <input
                  type="number"
                  min={0}
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  placeholder="e.g. 4096"
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                />
              </div>
              <div>
                <label className="text-gray-400 block mb-1">TopP (Optional)</label>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                  value={topP}
                  onChange={(e) => setTopP(e.target.value === '' ? 1 : parseFloat(e.target.value))}
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                />
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Rate limit / min (Optional)</label>
                <input
                  type="number"
                  min={0}
                  value={rateLimitPerMinute}
                  onChange={(e) => setRateLimitPerMinute(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  placeholder="e.g. 60"
                  className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-3">
            <input
              type="checkbox"
              checked={status}
              onChange={() => setStatus(!status)}
              className="accent-indigo-500"
            />
            <label className="text-sm text-gray-300">Set status as active</label>
          </div>
        </div>

        {saveError && (
          <p className="flex-shrink-0 px-6 py-2 text-sm text-red-400 border-t border-neutral-800">{saveError}</p>
        )}
        {/* Footer - always visible */}
        <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950 rounded-b-xl">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name || !provider || !model || !apiKey?.trim() || saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-md text-sm shadow disabled:opacity-40"
          >
            {saving ? 'Saving…' : isEdit ? 'Update Configuration' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddLLMConfigModal;
